import i18next from "i18next";
import { observable, makeObservable, action, runInAction, computed } from "mobx";
import {
    GetAttendanceByRealestateId,
    GetAttendanceByRealestateIdVariables
} from "src/api/generated/GetAttendanceByRealestateId";
import { GET_ATTENDANCE_BY_REALESTATEID } from "src/api/assembly";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { AssemblyRealestate } from "./AssemblyStore";
import { format } from "date-fns-tz";
import Axios from "src/network/Axios";
import { ENUM_TYPE } from "src/stores/EnumerationStore";
import { ensurePdfExtension, sanitizeAssemblyTitleForFileName } from "src/utils/Common";

export enum ATTENDANCE_STATE {
    PRESENT = "present",
    REPESENTED = "repesented",
    ABSENT = "absent"
}

export interface IAssemblyAttendances {
    tenantid: string;
    tenantname: string | null;
    attendancestate: IAttendanceState;
    unitid: string | null;
    unitname: string | null;
    value: string | null;
    realestateid: string | null;
    isFirstTenant: boolean;
    isunitchecked: boolean | null;
    istenantchecked: boolean | null;
}

export enum IAttendanceState {
    PRESENT = 0,
    ABSENT = 5,
    REPRESENTED = 1
}

export interface IAttendancesStates {
    id: string;
    key: IAttendanceState;
    label: string;
}

export interface IUpdatedTenantStates {
    tenantid: string;
    state: IAttendanceState;
}

export interface IAttendanceQuorumHeadInfo {
    presenceCounter: number;
    absenceCounter: number;
    totalCounter: number;
    presenceLabel: string;
    isHeadQuorumSatisfied: boolean;
}

export interface IAttendanceQuorumValueQuotaInfo {
    totalValueQuota: number;
    presentValueQuota: number;
    absenceValueQuota: number;
    valueQuotaLabel: string;
    isValueQuotaSatisfied: boolean;
}

export class AttendanceListStore {
    rootStore: RootStore;
    assemblyAttendances: IAssemblyAttendances[] = [];
    attendanceStates: IAttendancesStates[] = [];
    originalAssemblyAttendances: IAssemblyAttendances[] = [];
    selectedRealestateId: string = "";
    updatedTenantStates: IUpdatedTenantStates[] = [];
    attendanceFileId: string = "";
    isAttendancePDFDownloading: boolean = false;
    searchQuery: string = "";
    currentPage: number = 0;
    isActualizingAttendanceList: boolean = false;
    reRenderAttendanceListAfterActualization: boolean = false;
    isListViewPresent: boolean = true;

    // Non-observable guard: tracks which assembly's load is the active one so stale
    // async responses from a previous assembly are discarded.
    private activeLoadAssemblyId: string = "";

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            assemblyAttendances: observable,
            attendanceStates: observable,
            originalAssemblyAttendances: observable,
            selectedRealestateId: observable,
            updatedTenantStates: observable,
            attendanceFileId: observable,
            isAttendancePDFDownloading: observable,
            searchQuery: observable,
            currentPage: observable,
            isActualizingAttendanceList: observable,
            reRenderAttendanceListAfterActualization: observable,
            isListViewPresent: observable,

            filteredAssemblyAttendances: computed,
            areAllPresent: computed,
            areAllTenantChecked: computed,
            areAllUnitChecked: computed,
            hasCurrentAssemblyAttendancesChanged: computed,
            quorumHeadInfo: computed,
            quorumValueQuotaInfo: computed,
            selectedCurrentAssemblyRealestate: computed,
            itemsPerPage: computed,

            updateAttendanceState: action,
            updateAllAttendanceStates: action,
            updateTenantChecked: action,
            updateUnitChecked: action,
            updateAllTenantChecked: action,
            updateAllUnitChecked: action,
            fullReset: action,
            resetAttendanceList: action,
            saveAttendanceList: action,
            setAssemblyAttendances: action,
            loadAttendanceByRealestateId: action,
            setSelectedRealestateId: action,
            getItemPerPage: action,
            generateDownloadAttendanceList: action,
            generateAttendanceListPdf: action,
            downloadAttendanceListPdf: action,
            setAttendanceFileId: action,
            setIsAttendancePDFDownloading: action,
            setSearchQuery: action,
            checkDifferencesInAssemblyRealestateTenants: action,
            actualizeAssemblyRealestateTenants: action,
            checkAndActualizeAssemblyRealestateTenants: action,
            setCurrentPage: action,
            setIsActualizingAttendanceList: action,
            setReRenderAttendanceListAfterActualization: action,
            setIsListViewPresent: action
        });
    }

    get quorumHeadInfo(): IAttendanceQuorumHeadInfo {
        let response: IAttendanceQuorumHeadInfo = {
            presenceCounter: 0,
            absenceCounter: 0,
            totalCounter: 0,
            presenceLabel: `${0}/${0}`,
            isHeadQuorumSatisfied: false
        };

        if (this.assemblyAttendances?.length === 0) {
            return response;
        }

        const headQuotas = this.assemblyAttendances.filter(
            (value, index, self) => index === self.findIndex((i) => i.tenantid === value.tenantid)
        );

        const presentHeadQuotas = headQuotas.filter(
            (i) => i.attendancestate === IAttendanceState.PRESENT || i.attendancestate === IAttendanceState.REPRESENTED
        ).length;

        const absenceHeadQuotas = headQuotas.filter(
            (i) => i.attendancestate !== IAttendanceState.PRESENT && i.attendancestate !== IAttendanceState.REPRESENTED
        ).length;

        const totalHeadQuotas = headQuotas.length;

        response.totalCounter = totalHeadQuotas;
        response.presenceCounter = presentHeadQuotas;
        response.absenceCounter = absenceHeadQuotas;
        response.presenceLabel = `${presentHeadQuotas}/${totalHeadQuotas}`;

        if (
            (totalHeadQuotas <= 2 && presentHeadQuotas < totalHeadQuotas) || // 1. Wenn nur 1 oder 2  Kopfstimmen ingesgesamt, falls nicht alle anwesend --> false
            (totalHeadQuotas === 3 && presentHeadQuotas < 2) || // 2. Wenn 3 Kopfstimmen, falls nicht 2 anwesend sein --> false
            presentHeadQuotas < totalHeadQuotas / 2 // 3. Ab 4 Eigentümer falls nicht 50% anwesend ---> false
        ) {
            return response;
        }

        response.isHeadQuorumSatisfied = true;
        return response;
    }

    get quorumValueQuotaInfo(): IAttendanceQuorumValueQuotaInfo {
        const response = {
            isValueQuotaSatisfied: false,
            totalValueQuota: 0,
            presentValueQuota: 0,
            absenceValueQuota: 0,
            valueQuotaLabel: `${0}/${0}`
        } as IAttendanceQuorumValueQuotaInfo;

        if (this.assemblyAttendances?.length === 0) {
            return response;
        }

        const totalValueQuota = this.assemblyAttendances.reduce((acc, curr) => {
            if (curr.value) {
                return acc + parseFloat(curr.value);
            }
            return acc;
        }, 0);

        const presentValueQuota = this.assemblyAttendances
            .filter(
                (i) =>
                    i.attendancestate === IAttendanceState.PRESENT || i.attendancestate === IAttendanceState.REPRESENTED
            )
            .reduce((acc, curr) => {
                if (curr.value) {
                    return acc + parseFloat(curr.value);
                }
                return acc;
            }, 0);

        const absenceValueQuota = this.assemblyAttendances
            .filter(
                (i) =>
                    i.attendancestate !== IAttendanceState.PRESENT && i.attendancestate !== IAttendanceState.REPRESENTED
            )
            .reduce((acc, curr) => {
                if (curr.value) {
                    return acc + parseFloat(curr.value);
                }
                return acc;
            }, 0);

        response.totalValueQuota = Number(totalValueQuota.toFixed(4));
        response.presentValueQuota = Number(presentValueQuota.toFixed(4));
        response.absenceValueQuota = Number(absenceValueQuota.toFixed(4));
        response.valueQuotaLabel = `${response.presentValueQuota}/${response.totalValueQuota}`;

        if (response.presentValueQuota < response.totalValueQuota / 2) {
            return response;
        }

        response.isValueQuotaSatisfied = true;
        return response;
    }

    get selectedCurrentAssemblyRealestate(): AssemblyRealestate | undefined {
        return this.rootStore.assemblyStore.currentAssembly?.assemblyrealestates.find(
            (realestate) => realestate.realestate.id === this.selectedRealestateId
        );
    }

    get hasCurrentAssemblyAttendancesChanged(): boolean {
        if (this.assemblyAttendances.length !== this.originalAssemblyAttendances.length) {
            return true;
        }

        for (const filteredItem of this.filteredAssemblyAttendances) {
            const originalItem = this.originalAssemblyAttendances.find(
                (item) => item.unitid === filteredItem.unitid
            );

            if (
                !originalItem ||
                filteredItem.attendancestate !== originalItem.attendancestate ||
                filteredItem.istenantchecked !== originalItem.istenantchecked ||
                filteredItem.isunitchecked !== originalItem.isunitchecked
            ) {
                return true;
            }
        }
        return false;
    }

    get itemsPerPage(): number {
        const maxItems = 20;
        const minItems = 15;
        // If no items loaded, return the default maximum
        if (!this.assemblyAttendances || this.assemblyAttendances.length === 0) {
            return maxItems;
        }

        // UI.List.BasicList slices the full list with a fixed page size
        // (items.slice((page - 1) * itemsPerPage, page * itemsPerPage)), so this value
        // must stay constant across pages. Recalculating it per-page (as before) made the
        // slice boundaries drift as the user navigated, silently dropping tenants between
        // pages. We only ever compute the size of the first page here.
        return this.calculatePageSize(0, maxItems, minItems);
    }

    private calculatePageSize(startIndex: number, maxItems: number, minItems: number): number {
        if (startIndex >= this.assemblyAttendances.length) {
            return minItems;
        }

        let count = 0;
        let i = startIndex;

        while (i < this.assemblyAttendances.length) {
            const currentTenant = this.assemblyAttendances[i].tenantid;
            let groupCount = 0;

            while (i < this.assemblyAttendances.length && this.assemblyAttendances[i].tenantid === currentTenant) {
                groupCount++;
                i++;
            }

            if (count + groupCount > maxItems && count >= minItems) {
                break;
            }

            count += groupCount;
        }

        // Enforce minimum if there is enough data
        if (this.assemblyAttendances.length > minItems && count < minItems) {
            return minItems;
        }

        return count;
    }

    get filteredAssemblyAttendances(): IAssemblyAttendances[] {
        const query = this.searchQuery.trim().toLowerCase();
        // always apply the consolidation filter again (defensive) so consumers never see invalid records
        const base = this.filterAttendancesByConsolidation(this.assemblyAttendances);
        return query ? base.filter((a) => a.tenantname?.toLowerCase().includes(query)) : base;
    }

    get areAllPresent(): boolean {
        const uniqueTenants = this.assemblyAttendances.filter((a) => a.isFirstTenant);
        if (uniqueTenants.length === 0) return false;
        return uniqueTenants.every(
            (a) => a.attendancestate === IAttendanceState.PRESENT || a.attendancestate === IAttendanceState.REPRESENTED
        );
    }

    get areAllTenantChecked(): boolean {
        const uniqueTenants = this.assemblyAttendances.filter((a) => a.isFirstTenant);
        if (uniqueTenants.length === 0) return false;
        return uniqueTenants.every((a) => a.istenantchecked === true);
    }

    get areAllUnitChecked(): boolean {
        if (this.assemblyAttendances.length === 0) return false;
        return this.assemblyAttendances.every((a) => a.isunitchecked === true);
    }

    updateAttendanceState(tenantid: string, newStateId: string) {
        const newState = this.attendanceStates.find((s) => s.id === newStateId)?.key;
        if (newState == null) return;

        // 1) immutably update the live list
        this.assemblyAttendances = this.assemblyAttendances.map((attendance) =>
            attendance.tenantid === tenantid ? { ...attendance, attendancestate: newState } : attendance
        );

        // 2) record it for save/diff logic
        const original = this.originalAssemblyAttendances.find((o) => o.tenantid === tenantid);
        if (original && original.attendancestate === newState) {
            this.updatedTenantStates = this.updatedTenantStates.filter((u) => u.tenantid !== tenantid);
        } else {
            const idx = this.updatedTenantStates.findIndex((u) => u.tenantid === tenantid);
            if (idx > -1) this.updatedTenantStates[idx].state = newState;
            else this.updatedTenantStates.push({ tenantid, state: newState });
        }
    }

    updateAllAttendanceStates(newState: IAttendanceState) {
        // Get unique tenant IDs (only first occurrence per tenant)
        const uniqueTenantIds = this.assemblyAttendances.filter((a) => a.isFirstTenant).map((a) => a.tenantid);

        // Find the state ID for the given key
        const stateId = this.attendanceStates.find((s) => s.key === newState)?.id;
        if (!stateId) return;

        // Update each tenant's attendance state
        uniqueTenantIds.forEach((tenantid) => {
            this.updateAttendanceState(tenantid, stateId);
        });
    }

    updateTenantChecked(tenantid: string, checked: boolean) {
        this.assemblyAttendances = this.assemblyAttendances.map((a) =>
            a.tenantid === tenantid ? { ...a, istenantchecked: checked } : a
        );
    }

    updateUnitChecked(unitid: string | null, checked: boolean) {
        this.assemblyAttendances = this.assemblyAttendances.map((a) =>
            a.unitid === unitid ? { ...a, isunitchecked: checked } : a
        );
    }

    updateAllTenantChecked(checked: boolean) {
        this.assemblyAttendances = this.assemblyAttendances.map((a) =>
            a.isFirstTenant ? { ...a, istenantchecked: checked } : a
        );
    }

    updateAllUnitChecked(checked: boolean) {
        this.assemblyAttendances = this.assemblyAttendances.map((a) => ({ ...a, isunitchecked: checked }));
    }

    fullReset() {
        this.activeLoadAssemblyId = "";
        this.assemblyAttendances = [];
        this.originalAssemblyAttendances = [];
        this.updatedTenantStates = [];
        this.selectedRealestateId = "";
        this.searchQuery = "";
        this.currentPage = 0;
    }

    resetAttendanceList() {
        this.updatedTenantStates = [];
        this.setAssemblyAttendances(JSON.parse(JSON.stringify(this.originalAssemblyAttendances)));
    }

    private filterAttendancesByConsolidation(attendances: IAssemblyAttendances[]): IAssemblyAttendances[] {
        const isIT2 = !!(this.rootStore.navStore && (this.rootStore.navStore as any).isIT2);
        if (!isIT2) return attendances;

        const assemblyRealestates = this.rootStore.assemblyStore.currentAssembly?.assemblyrealestates ?? [];
        const consolidationMap = new Map<string, string | undefined>();
        assemblyRealestates.forEach((ar) => {
            const id = ar.realestate?.id;
            const cid = ar.realestate?.consolidationid;
            if (id) consolidationMap.set(id, cid ?? undefined);
        });

        return attendances.filter((a) => {
            const cid = a.realestateid ? consolidationMap.get(a.realestateid) : undefined;
            return cid != null && String(cid).trim() !== "";
        });
    }

    async loadAttendanceByRealestateId(assemblyId: string, realestateId: string) {
        this.activeLoadAssemblyId = assemblyId;
        try {
            const { data }: { data: GetAttendanceByRealestateId } = await apolloClientInstance.query<
                GetAttendanceByRealestateId,
                GetAttendanceByRealestateIdVariables
            >({
                query: GET_ATTENDANCE_BY_REALESTATEID,
                fetchPolicy: "no-cache",
                variables: {
                    assemblyid: assemblyId,
                    realestateid: realestateId
                }
            });

            // Discard response if the user navigated to a different assembly while the request was in flight
            if (assemblyId !== this.activeLoadAssemblyId) return;

            runInAction(() => {
                const seenTenantIds = new Set<string>();
                const mappedAttendances = data.assmeblyattendance.map((attendance) => {
                    const isFirst = !seenTenantIds.has(attendance.tenantid);
                    seenTenantIds.add(attendance.tenantid);
                    let attendanceState = attendance.attendancestate || 0;
                    // apply any overrides…
                    const updatedState = this.updatedTenantStates.find(
                        (updatedTenant) => updatedTenant.tenantid === attendance.tenantid
                    );
                    if (updatedState) attendanceState = updatedState.state;
                    return {
                        tenantid: attendance.tenantid,
                        tenantname: attendance.tenantname ?? "",
                        attendancestate: attendanceState,
                        unitid: attendance.unitid ?? null,
                        unitname: attendance.unitname ?? "",
                        value: attendance.value ?? "",
                        realestateid: attendance.realestateid ?? "",
                        isFirstTenant: isFirst,
                        isunitchecked: attendance.isunitchecked,
                        istenantchecked: attendance.istenantchecked
                    };
                });

                // 2. Set the live list
                this.setAssemblyAttendances(mappedAttendances);

                // 3. Snapshot the “original” list for resets/dirty-checks on first load per assembly
                // (originalAssemblyAttendances is cleared by fullReset() on assembly change)
                if (this.originalAssemblyAttendances.length === 0) {
                    this.originalAssemblyAttendances = JSON.parse(JSON.stringify(mappedAttendances));
                }

                // 4. Reset any search so computed shows the full list
                this.setSearchQuery("");

                // 5. And finally get attendanceStates from enums
                this.attendanceStates = this.rootStore.enumerationsStore
                    .getEnumByType(ENUM_TYPE.ASSEMBLYUSERPRESENCE)
                    .map((item) => ({
                        id: item.id,
                        key: item.key as IAttendanceState,
                        label: item.label
                    }));
            });
        } catch (error) {
            console.error(error);
        }
    }

    async saveAttendanceList(assemblyid: string) {
        const changedItems = this.assemblyAttendances.filter((current) => {
            const original = this.originalAssemblyAttendances.find((o) => o.unitid === current.unitid);
            return (
                !original ||
                current.attendancestate !== original.attendancestate ||
                current.istenantchecked !== original.istenantchecked ||
                current.isunitchecked !== original.isunitchecked
            );
        });

        const payload = {
            objects: changedItems.map((item) => ({
                assemblyid,
                tenantid: item.tenantid,
                unitid: item.unitid,
                state: item.attendancestate,
                istenantchecked: item.istenantchecked,
                isunitchecked: item.isunitchecked
            }))
        };

        try {
            await Axios.getAuthInstance().post(NetworkConfig.updateAssemblyRealestateTenantState, payload, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });

            runInAction(() => {
                this.updatedTenantStates = [];
                this.originalAssemblyAttendances = JSON.parse(JSON.stringify(this.assemblyAttendances));
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.attendance.save_success"),
                MessageType.SUCCESS
            );
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.attendance.failed_saving"),
                MessageType.ERROR
            );
            console.error("Error while trying to call updateAssemblyRealestateTenantState: ", error);
        }
    }

    getItemPerPage(): number {
        const maxItems = 15;
        // If no items loaded, return the default maximum
        if (!this.assemblyAttendances || this.assemblyAttendances.length === 0) {
            return maxItems;
        }

        let count = 0;
        let i = 0;

        while (i < this.assemblyAttendances.length) {
            // Get the current tenant id
            const currentTenant = this.assemblyAttendances[i].tenantid;
            let groupCount = 0;

            // Count consecutive items with the same tenant id
            while (i < this.assemblyAttendances.length && this.assemblyAttendances[i].tenantid === currentTenant) {
                groupCount++;
                i++;
            }

            // If adding the entire group would exceed our max, stop
            if (count + groupCount > maxItems) {
                break;
            }

            count += groupCount;
        }

        return count;
    }

    async generateDownloadAttendanceList(assemblyid: string) {
        this.setIsAttendancePDFDownloading(true);
        await this.generateAttendanceListPdf(assemblyid);
        await this.downloadAttendanceListPdf();
        this.setIsAttendancePDFDownloading(false);
    }

    generateAttendanceListPdf = async (assemblyId: string) => {
        try {
            const response = await Axios.getAuthInstance().post(
                NetworkConfig.generateattendancelistpdf + assemblyId,
                undefined,
                {
                    urlWithoutParams: NetworkConfig.generateattendancelistpdf,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            const result = response.data;
            this.setAttendanceFileId(JSON.parse(result.data));
        } catch (error) {
            this.setAttendanceFileId("");
            console.error("Error while trying to call generateattendancelistpdf: ", error);
        }
    };

    downloadAttendanceListPdf = async () => {
        try {
            const response = await Axios.getAuthInstance().get(
                `${NetworkConfig.downloadFileVersion2}/${this.attendanceFileId}`,
                {
                    urlWithoutParams: NetworkConfig.downloadFileVersion2,
                    responseType: "blob",
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            const assemblyDate = this.rootStore.assemblyStore.currentAssembly?.date;
            const date = assemblyDate ? format(new Date(assemblyDate), "dd_MM_yyyy") : "";
            const title = sanitizeAssemblyTitleForFileName(this.rootStore.assemblyStore.currentAssembly?.title);
            const fileName = ensurePdfExtension(`Präsenzliste_${title}_am_${date}`);

            // Start download
            const fileBlob = response.data;
            const objUrl = window.URL.createObjectURL(fileBlob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                MessageType.ERROR
            );
        }
    };

    async checkAndActualizeAssemblyRealestateTenants(assemblyid: string, reloadAttendanceAfterActualization: boolean) {
        this.setReRenderAttendanceListAfterActualization(false);
        this.setIsActualizingAttendanceList(true);
        const response = await this.checkDifferencesInAssemblyRealestateTenants(assemblyid);

        if (response && response.length > 0) {
            await this.actualizeAssemblyRealestateTenants(assemblyid);
        }
        if (reloadAttendanceAfterActualization && this.reRenderAttendanceListAfterActualization) {
            await this.loadAttendanceByRealestateId(assemblyid, this.selectedRealestateId);
        }
        this.setIsActualizingAttendanceList(false);
    }

    actualizeAssemblyRealestateTenants = async (assemblyId: string) => {
        try {
            const response = await Axios.getAuthInstance().post(
                `${NetworkConfig.actualizeAssemblyRealestateTenants}?assemblyid=${assemblyId}`,
                undefined,
                {
                    urlWithoutParams: NetworkConfig.actualizeAssemblyRealestateTenants,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            const result = response.data;
            this.setReRenderAttendanceListAfterActualization(
                result.cond_actualize_assembly_realestate_tenants_by_assemblyid &&
                    result.cond_actualize_assembly_realestate_tenants_by_assemblyid.length > 0
            );
        } catch (error) {
            this.setReRenderAttendanceListAfterActualization(false);
            console.error("Error while trying to call actualizeAssemblyRealestateTenants: ", error);
        }
    };

    checkDifferencesInAssemblyRealestateTenants = async (assemblyId: string) => {
        try {
            const response = await Axios.getAuthInstance().get(
                `${NetworkConfig.differencesAssemblyRealestateTenants}?assemblyid=${assemblyId}`,
                {
                    urlWithoutParams: NetworkConfig.differencesAssemblyRealestateTenants,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );
            const result = response.data;
            return result.cond_differences_assembly_realestate_tenants;
        } catch (error) {
            console.error("Error while trying to call differencesAssemblyRealestateTenants: ", error);
        }
    };

    /* SETTERS */

    setSelectedRealestateId(selectedRealestateId: string) {
        this.selectedRealestateId = selectedRealestateId;
    }

    setAssemblyAttendances = (assemblyAttendances: IAssemblyAttendances[]) => {
        this.assemblyAttendances = this.filterAttendancesByConsolidation(assemblyAttendances);
    };

    setOriginalAssemblyAttendances = (assemblyAttendances: IAssemblyAttendances[]) => {
        this.originalAssemblyAttendances = assemblyAttendances;
    };

    setAttendanceFileId = (attendanceFileId: string) => {
        this.attendanceFileId = attendanceFileId;
    };

    setIsAttendancePDFDownloading = (isAttendancePDFDownloading: boolean) => {
        this.isAttendancePDFDownloading = isAttendancePDFDownloading;
    };

    setSearchQuery = (searchQuery: string) => {
        this.searchQuery = searchQuery;
    };

    setCurrentPage = (page: number) => {
        this.currentPage = page;
    };

    setIsActualizingAttendanceList = (isActualizing: boolean) => {
        this.isActualizingAttendanceList = isActualizing;
    };

    setReRenderAttendanceListAfterActualization = (reRender: boolean) => {
        this.reRenderAttendanceListAfterActualization = reRender;
    };

    setIsListViewPresent = (isListViewPresent: boolean) => {
        this.isListViewPresent = isListViewPresent;
    };
}
